import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActionModel } from '../../../../../../core/lists/_models/action.model';
import { clone } from 'lodash-es';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { ActionService } from '../../../../../../core/lists/_services/action.service';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';

@Component({
  selector: 'kt-action-edit',
  templateUrl: './action-edit.component.html',
  styleUrls: ['./action-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ActionEditComponent implements OnInit {
  action: ActionModel;
  actionForm: FormGroup;
  customAPIForm: FormGroup;
  actionType = '';
  actionTypesList = [
    'FAX',
    'EMAIL',
    'CUSTOM_API',
    'CREATE_TASK',
    'DOCUMENT_GENERATOR',
    'MOVE_ACTIVITY_STEP',
  ];
  credentialControl = new FormControl();
  payloadKey = new FormControl();
  index = '';
  credentialAutocompleteCall = this.actionService.getCredentialsAutocomplete.bind(
    this.actionService,
  );
  @ViewChild('credentialAutocomplete') credentialAutocomplete: GeneralAutocompleteComponent;
  payloadKeysList: any[] = [];
  credentialObj: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ActionEditComponent>,
    private fb: FormBuilder,
    private actionService: ActionService,
    public stringUtils: StringUtilsService,
  ) {}

  ngOnInit() {
    this.action = this.data.action ? this.data.action : new ActionModel();
    this.createForm();
  }

  createForm() {
    this.actionType = this.action.type;
    // Update Action Mode
    this.customAPIForm = this.fb.group({
      url: [
        this.data?.action?.url || '',
        [
          Validators.required,
          Validators.pattern(
            '[(http(s)?):\\/\\/(www\\.)?a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:\\{\\}\\(\\)%_\\+.,~#?&//=]*)',
          ),
        ],
      ],
      http_method: [this.data?.action?.http_method || '', Validators.required],
    });
    if (this.actionType === 'CUSTOM_API' && this.data.mode === 'update') {
      this.customAPIForm?.get('http_method').setValue(this.action?.payload?.http_method || '');
      this.customAPIForm?.get('url').setValue(this.action?.payload?.url || '');
      this.payloadKeysList = this.action?.payload?.payload
        ? Object.keys(this.action.payload.payload)
        : [];
      if (this.action?.payload?.credential_id) {
        this.actionService.getCredentialByID(this.action.payload.credential_id).subscribe((res) => {
          this.credentialAutocomplete.setValue(res);
        });
      }
    }
    this.actionForm = this.fb.group({
      name: [this.action.name, Validators.required],
      type: [this.action.type, Validators.required],
    });
  }

  getErrorMessage(label: string): string {
    return `${label} field is required`;
  }
  getTitle() {
    if (this.data.mode === 'update') return 'Edit Action';
    return 'Create Action';
  }

  prepareAction(): ActionModel {
    const model = clone(this.actionForm.value);
    const _action = new ActionModel();
    _action.id = this.action.id;
    _action.name = model.name;
    _action.type = model.type;
    _action.payload = {};
    if (model?.type === 'CUSTOM_API') {
      _action.payload.credential_id = this.credentialObj?.id;
      _action.payload.http_method = this.customAPIForm.get('http_method')?.value;
      _action.payload.url = this.customAPIForm.get('url')?.value;
      _action.payload.payload = this.payloadKeysList.reduce((acc, currentValue) => {
        acc[currentValue] = '';
        return acc;
      }, {} as { [key: string]: string });
      return _action;
    }
    return _action;
  }

  onSubmit() {
    this.credentialAutocomplete?.updateValueAndValidity();
    this.actionForm.markAllAsTouched();
    this.customAPIForm?.markAllAsTouched();

    if (
      this.credentialAutocomplete?.isInvalid() ||
      this.actionForm.invalid ||
      (this.actionType === 'CUSTOM_API' && this.customAPIForm?.invalid)
    ) {
      return;
    }
    const editedAction = this.prepareAction();
    if (editedAction.id > 0) {
      this.updateAction(editedAction);
    } else {
      this.createAction(editedAction);
    }
  }

  updateAction(_action: ActionModel) {
    this.actionService.updateAction(_action).subscribe(() => {
      this.dialogRef.close({ updated: true });
    });
  }

  createAction(_action: ActionModel) {
    this.actionService.createAction(_action).subscribe(() => {
      this.dialogRef.close({ added: true });
    });
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control =
      this.actionType === 'CUSTOM_API'
        ? this.customAPIForm.controls[controlName]
        : this.actionForm.controls[controlName];

    if (!control) {
      return false;
    }

    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  showExternalLinkIcon() {
    const url = this.customAPIForm.get('url')?.value;
    return url && url.length > 0 && !this.isUrlError();
  }

  isUrlError(): boolean {
    const control = this.customAPIForm?.get('url');
    if (!control) {
      return false;
    }
    return control.hasError('pattern') && (control.dirty || control.touched);
  }

  close() {
    this.dialogRef.close();
  }

  onCredentialChange(val) {
    this.credentialObj = val;
  }
  addItem(event: Event): void {
    event.stopPropagation();
    const formValue = this.payloadKey.value;
    if (formValue === '' || !formValue) {
      return;
    }
    let checklistItems = [...this.payloadKeysList];

    if (this.index) {
      checklistItems = checklistItems.map((item, index) =>
        index === +this.index ? formValue : item,
      );
    } else {
      checklistItems.unshift(formValue);
    }
    this.payloadKeysList = checklistItems;
    this.index = '';
    this.payloadKey.setValue('');
  }
  cancelItemUpdate(event: Event): void {
    event.stopPropagation();
    this.index = '';
    this.payloadKey.setValue('');
  }
  updateItem(event: Event, idx: number): void {
    event.stopPropagation();

    const item = this.payloadKeysList.find((record, index) => index === idx);

    this.index = `${idx}`;
    this.payloadKey.patchValue(item);
  }
  deleteItem(event: Event, idx: number): void {
    event.stopPropagation();

    idx === 0 ? this.payloadKeysList.shift() : this.payloadKeysList.splice(idx, 1);
  }
}
